import { Download } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import Search from "../../../components/Search";
import {
  create,
  deleteMaterialType,
  exportAll,
  update,
  useList,
  isInUse,
} from "../../../services/materialType";
import { getLocal, getToken } from "../../../storage/auth";
import { MaterialType } from "../../../types/materialType";
import DataTable from "./components/DataTable";
import MaterialTypeForm from "./components/MaterialTypeForm";
import { hasClaim } from "../../../storage/userClaims";
import { useSnackbar } from "notistack";

interface IParams {
  page: number;
  keyword: string;
  sort: string;
  limit: number;
  token: string;
}
export default function MaterialTypes() {
  const { enqueueSnackbar } = useSnackbar();

  const [materialType, setMaterialType] = useState<MaterialType>({
    ATIVO: true,
  } as MaterialType);
  const [controlEdit, setControlEdit] = useState({
    loading: false,
    isInUse: false,
  });
  const [materialTypeId, setMaterialTypeId] = useState<string | number>();
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [token, setToken] = useState<string>();
  const [userValidation, setUserValidation] = useState<string>();
  const [readOnly, setReadOnly] = useState<boolean>(false);

  const getTokenSession = () => {
    const getTokens = (getToken()) as any;
    const getUser = getLocal("userInfo");
    setToken(getTokens);
    if (getUser) setUserValidation(JSON.parse(getUser));
  };
  useEffect(() => {
    getTokenSession();
  }, []);

  const [params, setParams] = useState<IParams>({
    page: 1,
    limit: 10,
  } as IParams);

  const [errors, setErrors] = useState<string[]>();
  const { data, meta, isLoading } = useList(params, token);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const handleRequest = (requestParams: any) => {
    setParams((prevState) => ({ ...prevState, ...requestParams }));
  };

  const handleEditMaterialType = async (id: string | number) => {
    setErrors([]);
    setReadOnly(false);
    const editMaterialType = data.find((item: any) => item.ID_TIPO_MATERIAL === id);

    if (editMaterialType) {
      setMaterialType(editMaterialType);
      setModalOpen(true);
    }

    try {
      setControlEdit({
        loading: true,
        isInUse: true,
      });
      const { inUse } = await isInUse(id);
      setControlEdit({
        loading: false,
        isInUse: inUse,
      });
    } catch (error) {
      enqueueSnackbar("It's not posible to edit, try again later", {
        variant: "error",
      });
      setControlEdit({
        loading: false,
        isInUse: true,
      });
    }
  };

  const handleDeleteMaterialTypeConfirmation = (id: string | number) => {
    setMaterialTypeId(id);
    setOpenDeleteModal(true);
  };

  const handleDeleteMaterialType = async () => {
    if (materialTypeId)
      try {
        setIsSaving(true);
        await deleteMaterialType(materialTypeId);
        enqueueSnackbar("Material Type deleted successful", {
          variant: "success",
        });
        setParams({ ...params });
      } catch (err: any) {
        if (err.response.status) {
          enqueueSnackbar("Material Type is being used", { variant: "error" });
        } else {
          enqueueSnackbar(err.response.data.message[0], { variant: "error" });
        }
      }
    setIsSaving(false);
    setOpenDeleteModal(false);
  };

  const handleFormSubmit = async (newMaterialType: MaterialType) => {
    try {
      setIsSaving(true);
      if (newMaterialType.ID_TIPO_MATERIAL) {
        await update(newMaterialType.ID_TIPO_MATERIAL, newMaterialType);
        enqueueSnackbar("Material Type updated successful", {
          variant: "success",
        });
      } else {
        await create(newMaterialType);
        enqueueSnackbar("Material Type created successful", {
          variant: "success",
        });
      }
      setMaterialType({ ATIVO: true } as MaterialType);
      setParams({ ...params });
      setModalOpen(false);
      setIsSaving(false);
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setErrors([err.response?.data?.message]);
      }
      setIsSaving(false);
    }
    setIsSaving(false);
  };

  const exportMaterialTypes = async () => {
    try {
      await exportAll(params);
    } catch (err) {}
  };

  const handleCancelEditMaterialType = () => {
    setMaterialType({} as MaterialType);
    setErrors([]);
    setModalOpen(false);
    setControlEdit({
      loading: false,
      isInUse: false,
    });
  };

  const onViewMaterialType = (id: string | number) => {
    setErrors([]);
    setReadOnly(true);
    const editMaterialType = data.find((item: any) => item.ID_TIPO_MATERIAL === id);

    if (editMaterialType) {
      setMaterialType(editMaterialType);
      setModalOpen(true);
    }
  };

  return (
    <Grid marginTop={4}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10}>
          <Typography variant="h5">Material Type</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" marginTop={1}>
        <Grid item xs={2}>
          {!modalOpen ? (
            <Grid item xs={2}>
              <Button
                variant="contained"
                onClick={() => {
                  setControlEdit({
                    loading: false,
                    isInUse: false,
                  });
                  setErrors([])
                  setModalOpen(true);
                }}
                fullWidth
                sx={{
                  display: hasClaim("Material Type - Maintenance")
                    ? "block"
                    : "none",
                }}
                data-testid="new-button"
              >
                New
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      {modalOpen ? (
        <Grid container alignItems="center">
          <MaterialTypeForm
            onSubmit={handleFormSubmit}
            materialType={materialType}
            onCancel={handleCancelEditMaterialType}
            isLoading={isSaving}
            errors={errors}
            controlEdit={controlEdit}
            readOnly={readOnly}
          />
        </Grid>
      ) : null}
      <Search onChange={handleRequest} />
      <Grid container marginTop={4} justifyContent="flex-end">
        <Button
          variant="text"
          onClick={exportMaterialTypes}
          startIcon={<Download />}
        >
          Export XLSX
        </Button>
      </Grid>
      <Grid>
        <DataTable
          loading={isLoading}
          data={data}
          userValidation={userValidation}
          onDeleteMaterialType={handleDeleteMaterialTypeConfirmation}
          onEditMaterialType={handleEditMaterialType}
          onViewMaterialType={onViewMaterialType}
          rowCount={meta.total || 0}
          onPaginationModelChange={(e: any) => {
            setPaginationModel(e);
            handleRequest({ page: e.page + 1, limit: e.pageSize });
          }}
          paginationModel={paginationModel}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: params.limit } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
        />
      </Grid>
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Material Type</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure that you want to delete this Material Type?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button onClick={handleDeleteMaterialType} data-testid="delete-modal-confirm">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
